import React, { useState } from 'react';
import { Eye, EyeOff, AlertCircle, Loader2, Lock } from 'lucide-react';

interface LoginScreenProps {
  loginInput: { email: string; password: string };
  setLoginInput: (valor: { email: string; password: string }) => void;
  handleLogin: (e: React.FormEvent) => void;
  loginError: string;
  isSending: boolean;
}

// Tela de entrada: painel institucional (desktop) + formulário de login
export const LoginScreen: React.FC<LoginScreenProps> = ({ loginInput, setLoginInput, handleLogin, loginError, isSending }) => {
  const [mostrarSenha, setMostrarSenha] = useState(false);

  return (
    <div className="min-h-screen bg-white lg:grid lg:grid-cols-[minmax(0,5fr)_minmax(0,6fr)]">
      {/* Painel institucional (desktop) */}
      <aside className="hidden lg:flex flex-col justify-between bg-[#08162D] text-white px-14 py-12 relative overflow-hidden">
        <div className="absolute inset-x-0 top-0 h-1.5 bg-[#D92B2B]" aria-hidden="true" />
        <img src="/img/brasao-3-texto-branco.png" alt="Governo do Estado de São Paulo – Secretaria de Estado da Saúde" className="h-20 w-auto self-start" />
        <div className="max-w-md">
          <p className="text-sm font-semibold text-[#F08A8A] uppercase tracking-[0.18em]">Emendas Parlamentares 2026</p>
          <h2 className="text-4xl font-bold leading-tight mt-3">Plano de Trabalho</h2>
          <p className="text-[#B8C1D1] text-base leading-relaxed mt-4">
            Cadastro, envio e acompanhamento dos planos de trabalho das emendas destinadas aos estabelecimentos de saúde.
          </p>
          <ol className="mt-10 space-y-4">
            {[
              ['Emenda disponibilizada', 'A SES-SP libera a emenda para o seu CNES'],
              ['Preenchimento', 'Você completa as 7 seções do plano'],
              ['Envio assinado', 'O PDF assinado segue para análise'],
            ].map(([titulo, desc], i) => (
              <li key={titulo} className="flex gap-4">
                <span className="flex-shrink-0 w-8 h-8 rounded-full border border-white/25 text-sm font-bold flex items-center justify-center">{i + 1}</span>
                <div>
                  <p className="font-semibold leading-tight">{titulo}</p>
                  <p className="text-sm text-[#B8C1D1] mt-0.5">{desc}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
        <p className="text-xs text-[#8D99AE]">Secretaria de Estado da Saúde de São Paulo · Grupo de Controle Financeiro – Emendas Federais</p>
      </aside>

      {/* Formulário */}
      <div className="min-h-screen flex flex-col items-center justify-center px-5 py-10 sm:px-8">
        <div className="w-full max-w-sm">
          <div className="lg:hidden text-center mb-10">
            <img src="/img/brasao-1.png" alt="Governo do Estado de São Paulo – Secretaria de Estado da Saúde" className="h-16 w-auto mx-auto mb-6" />
            <p className="text-xs font-semibold text-[#B71F1F] uppercase tracking-[0.18em]">Emendas Parlamentares 2026</p>
            <h2 className="text-2xl font-bold text-slate-900 mt-1">Plano de Trabalho</h2>
          </div>

          <h1 className="text-2xl font-bold text-slate-900">Entrar</h1>
          <p className="text-sm text-slate-600 mt-1 mb-8">Use o e-mail e a senha cadastrados pela SES-SP.</p>

          <form onSubmit={handleLogin} className="space-y-5">
            <div>
              <label htmlFor="login-email" className="block text-sm font-semibold text-slate-800 mb-1.5">E-mail</label>
              <input
                id="login-email"
                type="email"
                autoComplete="username"
                inputMode="email"
                autoFocus
                className="w-full h-12 px-4 bg-white border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#0B57D0]/25 focus:border-[#0B57D0] text-slate-900 placeholder-slate-400 text-[15px]"
                value={loginInput.email}
                onChange={(e) => setLoginInput({ ...loginInput, email: e.target.value })}
                placeholder="nome@instituicao.org.br"
                aria-invalid={!!loginError}
                aria-describedby={loginError ? 'login-erro' : undefined}
                required
              />
            </div>

            <div>
              <label htmlFor="login-senha" className="block text-sm font-semibold text-slate-800 mb-1.5">Senha</label>
              <div className="relative">
                <input
                  id="login-senha"
                  type={mostrarSenha ? 'text' : 'password'}
                  autoComplete="current-password"
                  className="w-full h-12 pl-4 pr-12 bg-white border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#0B57D0]/25 focus:border-[#0B57D0] text-slate-900 placeholder-slate-400 text-[15px]"
                  value={loginInput.password}
                  onChange={(e) => setLoginInput({ ...loginInput, password: e.target.value })}
                  aria-invalid={!!loginError}
                  aria-describedby={loginError ? 'login-erro' : undefined}
                  required
                />
                <button
                  type="button"
                  onClick={() => setMostrarSenha(v => !v)}
                  className="absolute right-1 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-100"
                  aria-label={mostrarSenha ? 'Ocultar senha' : 'Mostrar senha'}
                  aria-pressed={mostrarSenha}
                >
                  {mostrarSenha ? <EyeOff className="w-4.5 h-4.5" style={{ width: 18, height: 18 }} /> : <Eye className="w-4.5 h-4.5" style={{ width: 18, height: 18 }} />}
                </button>
              </div>
            </div>

            {loginError && (
              <div id="login-erro" role="alert" className="flex gap-2.5 p-3.5 bg-red-50 rounded-xl border border-red-200">
                <AlertCircle className="w-4.5 h-4.5 text-red-600 flex-shrink-0 mt-0.5" style={{ width: 18, height: 18 }} aria-hidden="true" />
                <p className="text-sm text-red-800">{loginError}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={isSending}
              className="w-full h-12 bg-[#D92B2B] text-white font-bold rounded-xl hover:bg-[#B71F1F] text-[15px] flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {isSending ? <><Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> Entrando...</> : 'Entrar'}
            </button>
          </form>

          <div className="mt-10 pt-6 border-t border-slate-200 flex items-start gap-2.5 text-sm text-slate-600">
            <Lock className="w-4 h-4 flex-shrink-0 mt-0.5 text-slate-400" aria-hidden="true" />
            <p>
              Acesso restrito a usuários cadastrados. Problemas para entrar? Escreva para{' '}
              <a href="mailto:gcf-emendasfederais@saude.sp.gov.br" className="font-medium text-[#0B3B8C] underline underline-offset-2 break-words">gcf-emendasfederais@saude.sp.gov.br</a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
